import { useEffect, useState } from 'react';

import { Button, Input, Select, useDisclosure, useToast } from '@chakra-ui/react';
import { HttpStatusCode } from 'axios';
import { useNavigate } from 'react-router-dom';

import AddIcon from '@/assets/AddIcon';
import ModalDelete from '@/components/Modals/ModalDelete';
import TableInspectorPerson from '@/components/Tables/TableInspectorPerson';
import { COLORS, DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, DURATION_TOAST } from '@/common/constants';
import { useAppSelector } from '@/redux/hooks';
import { RootState } from '@/redux/store';
// import { setDataTotalNewInspector } from '@/redux/slices/notificationSlice';
import { deleteInspectorPerson, getListInspectorPerson } from '@/services/inspectorPersonService';

import '../styles/ListPage.css'

const InspectorPersonList: React.FC = () => {
    const navigate = useNavigate();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();
    const name = useAppSelector((state: RootState) => state.account.name);
    // const dispatch = useAppDispatch();

    const [filters, setFilters] = useState({
        search: '',
        status: '',
        type: '',
    });

    const [dataList, setDataList] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [idToDelete, setIdToDelete] = useState<string>('');
    const [currentPageNumber, setCurrentPageNumber] = useState(DEFAULT_PAGE_NUMBER);
    const [currentPageSize, setCurrentPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [totalPageNumber, setTotalPageNumber] = useState(1);
    const [hasNextPage, setHasNextPage] = useState(false);

    const showToast = (status: 'success' | 'error', title: string, description: string) => {
        toast({
            title,
            description,
            status,
            duration: DURATION_TOAST,
            isClosable: true,
        });
    };

    const handleDelete = async () => {
        const obj = { id: idToDelete, modifiedBy: name || 'System' };

        try {
            const response = await deleteInspectorPerson(obj) as any;
            if (response.status === HttpStatusCode.Ok) {
                await fetchInspectors();
                showToast('success', 'Inspector deleted.', "We've deleted the inspector for you.");
            } else {
                showToast('error', response?.status || 'Inspector failed to delete.', response?.message || "We failed to delete the inspector for you.");
            }
        } catch {
            showToast('error', 'Error deleting inspector', 'An error occurred while deleting the inspector.');
        } finally {
            onClose();
        }
    };

    const handleFilterChange = (field: keyof typeof filters) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setFilters((prev) => ({ ...prev, [field]: e.target.value }));
    };

    // const handlePaginationChange = (field: keyof typeof pagination) => (value: number) => {
    //     setPagination((prev) => ({ ...prev, [field]: value }));
    // };

    const fetchInspectors = async () => {
        setIsLoading(true);
        const { search, status, type } = filters;

        const params = {
            search,
            pageNumber: currentPageNumber,
            pageSize: currentPageSize,
            status,
            type,
        };

        try {
            const response = await getListInspectorPerson(params) as any;
            if (response.status === HttpStatusCode.Ok) {
                setDataList(response.data);
                setTotalPageNumber(response.totalPage);
                setHasNextPage(response.hasNextPage);
                // dispatch(setDataTotalNewInspector(response?.recordCount || 0));
            } else {
                showToast('error', response.error.code, response.error.message);
            }
        } catch {
            showToast('error', 'Error fetching data', 'An error occurred while fetching data.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchInspectors();
    }, [currentPageSize, currentPageNumber]);

    return (
        <>
            <ModalDelete title="Delete it?" isOpen={isOpen} onClose={onClose} handleConfirmDelete={handleDelete} />
            <div className="table-container">
                <div className="table-filter-action-container">
                    <div className="table-filter-container">
                        <div className="table-filter-input filter-input-width-5">
                            <Input
                                placeholder='Search'
                                onChange={handleFilterChange('search')}
                            />
                        </div>
                        <div className="table-filter-input filter-input-width-3">
                            <Select
                                placeholder='All Status'
                                onChange={handleFilterChange('status')}
                            >
                                <option value="true">Active</option>
                                <option value="false">Inactive</option>
                            </Select>
                        </div>
                        <div className="table-filter-input filter-input-width-3">
                            <Select
                                placeholder='All Type'
                                onChange={handleFilterChange('type')}
                            >
                                <option value="SIRE">SIRE</option>
                                <option value="Vetting">Vetting</option>
                            </Select>
                        </div>
                        <div>
                            <Button
                                className='table-search-button'
                                bgColor={COLORS.BUTTON.BACKGROUND}
                                color={COLORS.BUTTON.FOREGROUND}
                                variant='solid'
                                borderRadius={10}
                                onClick={fetchInspectors}
                            >
                                Search
                            </Button>
                        </div>
                        <div className="table-filter-separator"/>
                        <Button
                            leftIcon={<AddIcon />}
                            bgColor={COLORS.BUTTON.BACKGROUND}
                            color={COLORS.BUTTON.FOREGROUND}
                            className='table-add-button'
                            variant='solid'
                            borderRadius={10}
                            onClick={() => navigate('/manage-inspector/add')}
                        >
                            Add Inspector
                        </Button>
                    </div>
                </div>
                <TableInspectorPerson
                    dataList={dataList}
                    isLoading={isLoading}
                    handleEdit={(id: string) => navigate('/manage-inspector/edit', { state: { id } })}
                    handleDelete={(id: string) => {
                        setIdToDelete(id);
                        onOpen();
                    }}
                    currentPageSize={currentPageSize}
                    handleChangePageSize={setCurrentPageSize}
                    handleChangePageNumber={setCurrentPageNumber}
                    currentPageNumber={currentPageNumber}
                    totalPageNumber={totalPageNumber}
                    hasNextPage={hasNextPage}
                />
            </div>
        </>
    );
};

export default InspectorPersonList;
